import type { Document } from '@features/documents/api/types/document.types';

import { useDocumentsFilter } from '@features/documents/contexts/documents-filter/useDocumentsFilter';
import { useDocuments } from '@features/documents/hooks/use-documents/useDocuments';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { SubmittedDocumentsContentProps } from './SubmittedDocumentsContent.types';

import { SubmittedDocumentsTable } from './submitted-documents-table/SubmittedDocumentsTable';
import styles from './SubmittedDocumentsContent.module.css';
import { filterDocumentsByName } from './SubmittedDocumentsContent.utils';

const SubmittedDocumentsContent = React.memo(
  ({
    organizationId,
    canDownload,
  }: Readonly<SubmittedDocumentsContentProps>): React.ReactElement => {
    const { filters, isFiltersApplied } = useDocumentsFilter();
    const { data, isLoading, isError } = useDocuments({
      documentType: 'Submitted',
      organizationId,
    });

    const filteredDocuments: Document[] = React.useMemo(() => {
      if (!data?.documents) return [];
      if (!filters.searchText) return data.documents;
      return filterDocumentsByName(data.documents, filters.searchText);
    }, [data?.documents, filters.searchText]);

    if (isLoading) {
      return <ContentLoader />;
    }

    if (isError) {
      return <InlineFeedback type="data-error" />;
    }

    if (!data || data.totalCount === 0) {
      return <InlineFeedback type="so-empty" />;
    }

    if (filteredDocuments.length === 0 && isFiltersApplied) {
      return <InlineFeedback type="no-search-results" />;
    }

    return (
      <div className={styles.submittedDocumentsContent}>
        <SubmittedDocumentsTable
          documents={filteredDocuments}
          organizationId={organizationId}
          canDownload={canDownload}
        />
      </div>
    );
  }
);

SubmittedDocumentsContent.displayName = 'SubmittedDocumentsContent';

export { SubmittedDocumentsContent };
